import React, { useRef, useState, useEffect, useCallback } from 'react';

interface SignaturePadProps {
  width?: number;
  height?: number;
  onSignature: (dataUrl: string | null) => void;
  savedSignature?: string | null;
}

export function SignaturePad({ width = 400, height = 150, onSignature, savedSignature }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [useSaved, setUseSaved] = useState(false);

  const drawnRef = useRef(false);

  const getCtx = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext('2d');
  }, []);

  const clearCanvas = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Draw baseline
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(20, height - 30);
    ctx.lineTo(width - 20, height - 30);
    ctx.stroke();
    ctx.setLineDash([]);
    // Label
    ctx.fillStyle = '#999';
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.fillText('Sign here', 20, height - 12);
    setHasDrawn(false);
    setUseSaved(false);
    drawnRef.current = false;
    onSignature(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCtx, width, height]);

  // Only clear on mount, not on every re-render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { clearCanvas(); }, []);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const touch = e.touches[0];
      return { x: (touch.clientX - rect.left) * scaleX, y: (touch.clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = getCtx();
    if (!ctx) return;
    setIsDrawing(true);
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = getCtx();
    if (!ctx) return;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    if (!hasDrawn) setHasDrawn(true);
    drawnRef.current = true;
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas && drawnRef.current) {
      onSignature(canvas.toDataURL('image/png'));
    }
  };

  const applySaved = () => {
    if (!savedSignature) return;
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Scale to fit
      const scale = Math.min((canvas.width - 40) / img.width, (canvas.height - 40) / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      setHasDrawn(true);
      setUseSaved(true);
      onSignature(canvas.toDataURL('image/png'));
    };
    img.src = savedSignature;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          width: '100%',
          maxWidth: `${width}px`,
          height: 'auto',
          aspectRatio: `${width}/${height}`,
          borderRadius: '8px',
          border: '1px solid #334155',
          cursor: 'crosshair',
          touchAction: 'none',
        }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.75rem' }}>
        <button
          type="button"
          onClick={clearCanvas}
          style={{
            padding: '4px 12px', borderRadius: '4px', border: '1px solid #334155',
            background: '#1e293b', color: '#94a3b8', cursor: 'pointer', fontSize: '0.75rem',
          }}
        >
          Clear
        </button>
        {savedSignature && !useSaved && (
          <button
            type="button"
            onClick={applySaved}
            style={{
              padding: '4px 12px', borderRadius: '4px', border: '1px solid #334155',
              background: '#1e293b', color: '#f97316', cursor: 'pointer', fontSize: '0.75rem',
            }}
          >
            Use Saved Signature
          </button>
        )}
        {hasDrawn && (
          <span style={{ color: '#22c55e', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Signed
          </span>
        )}
      </div>
    </div>
  );
}
